// Trackpad emits small (~5–20px) frequent pixel deltas; mouse-drag is 1:1
// pixel→delta, so dampen wheel-derived rotation to avoid hyper-sensitivity.
const TRACKPAD_SENSITIVITY = 0.6;

export class MouseHandler {
    private el: HTMLElement;
    private clickCb: ((x: number, y: number) => void) | null = null;
    private dragCb: ((dx: number, dy: number) => void) | null = null;
    private scrollCb: ((d: number) => void) | null = null;
    private rightDrag = false;
    private pointerLocked = false;
    /**
     * Set when {@link startOrbiting} requested pointer lock so the
     * pointerlockchange handler can distinguish a MouseHandler-owned lock from
     * one acquired by {@link PointerLockController}. Without this, FPS-mode
     * lock would flip `pointerLocked = true` and `isOrbiting()` would return
     * true → MouseHandler's mousemove path would double-apply rotation on top
     * of PointerLockController's own delta callback.
     */
    private didRequestLock = false;
    private lastX = 0;
    private lastY = 0;
    private handlers: { [k: string]: (e: any) => void } = {};
    private documentHandlers: { [k: string]: (e: any) => void } = {};

    constructor(domElement: HTMLElement) {
        this.el = domElement;
        this.handlers = {
            click: (e: MouseEvent) => {
                if (e.button === 0 && !this.isModifiedLook(e)) this.clickCb?.(e.clientX, e.clientY);
            },
            contextmenu: (e: Event) => e.preventDefault(),
            mousedown: (e: MouseEvent) => {
                if (this.isLookButton(e)) {
                    this.startOrbiting(e);
                }
            },
            mousemove: (e: MouseEvent) => {
                this.handleMouseMove(e);
            },
            mouseup: (e: MouseEvent) => {
                if (this.isLookButton(e) || e.buttons === 0) this.stopOrbiting();
            },
            // Wheel events come from three sources:
            //   1. macOS pinch-to-zoom synthesizes ctrlKey=true (no real ctrl).
            //   2. Notched mouse wheels report deltaMode=DOM_DELTA_LINE/PAGE.
            //   3. Trackpad two-finger swipes report deltaMode=DOM_DELTA_PIXEL
            //      with meaningful deltaX/deltaY — route to camera rotation so
            //      macOS Chrome users can orbit without holding a button.
            wheel: (e: WheelEvent) => {
                e.preventDefault();
                if (e.ctrlKey || e.deltaMode !== 0) {
                    this.scrollCb?.(e.deltaY);
                    return;
                }
                this.dragCb?.(e.deltaX * TRACKPAD_SENSITIVITY, e.deltaY * TRACKPAD_SENSITIVITY);
            },
        };
        this.documentHandlers = {
            mousedown: (e: MouseEvent) => {
                if (this.isLookButton(e)) this.startOrbiting(e);
            },
            mousemove: (e: MouseEvent) => {
                if (this.isOrbiting() && e.target !== this.el) this.handleMouseMove(e);
            },
            mouseup: this.handlers.mouseup,
            pointerlockchange: () => {
                const isLockedToEl = document.pointerLockElement === this.el;
                // Only treat ourselves as pointer-locked when WE requested it.
                // If PointerLockController acquired the lock, we leave
                // `pointerLocked` false so isOrbiting() stays false → no
                // double-rotation. When the lock is released entirely, reset
                // both flags regardless of origin.
                if (!isLockedToEl) {
                    this.pointerLocked = false;
                    this.didRequestLock = false;
                } else if (this.didRequestLock) {
                    this.pointerLocked = true;
                }
            },
        };
    }

    onClick(cb: (x: number, y: number) => void): void { this.clickCb = cb; }
    onDrag(cb: (dx: number, dy: number) => void): void { this.dragCb = cb; }
    onScroll(cb: (d: number) => void): void { this.scrollCb = cb; }

    attach(): void {
        for (const [k, v] of Object.entries(this.handlers)) {
            this.el.addEventListener(k, v, { passive: false });
        }
        for (const [k, v] of Object.entries(this.documentHandlers)) {
            document.addEventListener(k, v, { passive: false });
        }
    }

    detach(): void {
        for (const [k, v] of Object.entries(this.handlers)) {
            this.el.removeEventListener(k, v);
        }
        for (const [k, v] of Object.entries(this.documentHandlers)) {
            document.removeEventListener(k, v);
        }
        this.stopOrbiting();
    }

    private isOrbiting(): boolean {
        return this.rightDrag || this.pointerLocked;
    }

    private isModifiedLook(e: MouseEvent): boolean {
        return e.ctrlKey || e.altKey || e.metaKey;
    }

    private isLookButton(e: MouseEvent): boolean {
        return e.button === 2 || e.button === 1 || (e.button === 0 && this.isModifiedLook(e));
    }

    private startOrbiting(e: MouseEvent): void {
        e.preventDefault();
        const wasOrbiting = this.isOrbiting();
        this.rightDrag = true;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
        this.el.dataset.cameraLookActive = 'true';
        if (wasOrbiting) return;

        this.didRequestLock = true;
        const lockRequest = this.el.requestPointerLock?.();
        if (lockRequest instanceof Promise) {
            lockRequest.catch(() => { this.didRequestLock = false; });
        }
    }

    private handleMouseMove(e: MouseEvent): void {
        if (!this.isOrbiting()) return;
        const dx = this.pointerLocked ? e.movementX : e.clientX - this.lastX;
        const dy = this.pointerLocked ? e.movementY : e.clientY - this.lastY;
        if (dx !== 0 || dy !== 0) {
            this.el.dataset.cameraLookDelta = `${dx.toFixed(1)},${dy.toFixed(1)}`;
            this.dragCb?.(dx, dy);
        }
        this.lastX = e.clientX;
        this.lastY = e.clientY;
    }

    private stopOrbiting(): void {
        this.rightDrag = false;
        this.el.dataset.cameraLookActive = 'false';
        // Only release pointer-lock if WE acquired it. Otherwise
        // PointerLockController owns the lock and ESC / its own teardown is
        // responsible for exiting.
        if (this.didRequestLock && this.pointerLocked && document.pointerLockElement === this.el) {
            document.exitPointerLock?.();
        }
        this.pointerLocked = false;
        this.didRequestLock = false;
    }
}
